/**
 * One card's writes in flight: which act is sending, which the server refused, which it accepted —
 * and the rule that a write which RESOLVES the item strikes the card only once the server agreed.
 *
 * It is `model/implicitClose` applied to the writes a card now makes on the spot ("Dodaj tydzień",
 * "Zakończ plan", "+2 tygodnie", "Wyślij ponownie"…): nothing is optimistic, a second press while
 * the first is in the air sends nothing, a refusal is said in the live region AND left in ink beside
 * the control (this app has no toasts), and an accepted act is not sent twice.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  implicitCloseIsSending,
  implicitCloseRefused,
  implicitCloseSucceeded,
  implicitCloseWasRefused,
  NOTHING_CLOSING,
  startImplicitClose,
} from '../model/implicitClose';

export interface TaskActRun {
  /** Which act — unique within the card. */
  readonly act: string;
  readonly write: () => Promise<void>;
  /** The write makes the item stop being true (attention-queue R5): strike the card on success. */
  readonly closes: boolean;
  /** What the live region says when it lands. */
  readonly done: string;
}

export interface TaskAct {
  readonly run: (run: TaskActRun) => void;
  readonly isSending: (act: string) => boolean;
  readonly wasRefused: (act: string) => boolean;
  readonly isDone: (act: string) => boolean;
  /** Any act on this card is in the air — every other control on it waits. */
  readonly busy: boolean;
}

export function useTaskAct({
  onResolved,
  announce,
}: {
  readonly onResolved: () => void;
  readonly announce: (message: string) => void;
}): TaskAct {
  const { t } = useTranslation();
  const [state, setState] = useState(NOTHING_CLOSING);

  return {
    run: ({ act, write, closes, done }) => {
      const next = startImplicitClose(state, act);
      if (next === null) return;
      setState(next);
      write().then(
        () => {
          setState((current) => implicitCloseSucceeded(current, act));
          announce(done);
          if (closes) onResolved();
        },
        () => {
          setState((current) => implicitCloseRefused(current, act));
          announce(t('web:home.live.quickFailed'));
        },
      );
    },
    isSending: (act) => implicitCloseIsSending(state, act),
    wasRefused: (act) => implicitCloseWasRefused(state, act),
    isDone: (act) => state.closed.has(act),
    busy: state.sending.size > 0,
  };
}
